import React, { useState, useEffect } from 'react';
import { X, Navigation, Loader2, CheckCircle2, Play, AlertCircle, MapPin } from 'lucide-react';
import { supabase } from '../supabase';
import { Contact } from '../types';
import { CONFIG } from '../config';

interface BulkGeocodeModalProps {
  contacts: Contact[];
  onClose: () => void;
  onComplete: () => void;
}

const BulkGeocodeModal: React.FC<BulkGeocodeModalProps> = ({ contacts, onClose, onComplete }) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentContact, setCurrentContact] = useState<string>('');
  const [results, setResults] = useState({ success: 0, failed: 0 });
  const [completed, setCompleted] = useState(false);

  const startGeocoding = async () => {
    setProcessing(true);
    let successCount = 0;
    let failCount = 0;

    console.log('[BulkGeocode] Iniciando processo via Fetch Manual (Authorization: Bearer ANON_KEY)');

    for (let i = 0; i < contacts.length; i++) {
      const contact = contacts[i];
      setCurrentContact(contact.name);
      setProgress(Math.round(((i + 1) / contacts.length) * 100));

      if (!contact.street || !contact.city) {
        failCount++;
        continue;
      }

      try {
        const requestId = crypto.randomUUID();
        
        // CORREÇÃO: Adicionado Authorization: Bearer <anon_key>
        // O Gateway do Supabase exige esse header mesmo para chamadas públicas/anônimas.
        const response = await fetch(`${CONFIG.supabaseUrl}/functions/v1/geocode-address`, {
          method: 'POST',
          credentials: 'omit', // Mantém 'omit' para não enviar cookies de sessão inválidos
          headers: {
            'Content-Type': 'application/json',
            'apikey': CONFIG.supabaseAnonKey,
            'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`, // Header Obrigatório pelo Gateway
            'x-request-id': requestId
          },
          body: JSON.stringify({
            street: contact.street,
            number: contact.number,
            neighborhood: contact.neighborhood,
            city: contact.city,
            zipcode: contact.zipcode
          })
        });

        console.log(`[BulkGeocode] Req: ${requestId}, Status: ${response.status}`);

        if (!response.ok) {
          console.error(`[BulkGeocode] Erro HTTP: ${response.status} ${response.statusText}`);
          failCount++;
          continue;
        }

        const data = await response.json();

        if (data && data.ok && data.latitude && data.longitude) {
          await supabase.from('contacts').update({
            latitude: data.latitude,
            longitude: data.longitude
          }).eq('id', contact.id);
          successCount++;
        } else {
          // Fallback: BrasilAPI via CEP (gratuito, boa cobertura no interior BR)
          let geocodedViaCep = false;
          const cep = String(contact.zipcode || '').replace(/\D/g, '');
          if (cep.length === 8) {
            try {
              const brasilApiRes = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
              if (brasilApiRes.ok) {
                const brasilApiData = await brasilApiRes.json();
                if (brasilApiData?.latitude && brasilApiData?.longitude) {
                  console.log(`[BulkGeocode] BrasilAPI encontrou ${contact.name}: ${brasilApiData.city}`);
                  await supabase.from('contacts').update({
                    latitude: parseFloat(brasilApiData.latitude),
                    longitude: parseFloat(brasilApiData.longitude)
                  }).eq('id', contact.id);
                  successCount++;
                  geocodedViaCep = true;
                }
              }
            } catch (cepErr) {
              console.warn(`[BulkGeocode] BrasilAPI falhou para ${contact.name}:`, cepErr);
            }
          }
          if (!geocodedViaCep) {
            console.warn(`[BulkGeocode] Falha total para ${contact.name}:`, data?.error);
            failCount++;
          }
        }
      } catch (e: any) {
        console.error(`[BulkGeocode] Exceção de rede ao geocodificar ${contact.name}:`, e);
        failCount++;
      }
      
      // Delay para evitar Rate Limiting do Google
      await new Promise(resolve => setTimeout(resolve, 250));
    }

    setResults({ success: successCount, failed: failCount });
    setProcessing(false);
    setCompleted(true);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-navy-950/80 backdrop-blur-md animate-in fade-in" onClick={processing ? undefined : onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-md rounded-[32px] shadow-2xl p-8 border border-neutral-200 dark:border-navy-800 text-center" onClick={e => e.stopPropagation()}>
        
        {!completed ? (
          <>
            <div className="w-20 h-20 bg-parliament-50 dark:bg-navy-800 text-parliament-600 dark:text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner relative">
               {processing ? (
                 <>
                   <Loader2 size={40} className="animate-spin text-parliament-600" />
                   <span className="absolute text-[10px] font-black">{progress}%</span>
                 </>
               ) : (
                 <Navigation size={40} />
               )}
            </div>
            
            <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-2">
              {processing ? 'Localizando Contatos...' : 'Localizar em Lote'}
            </h3>
            
            <p className="text-slate-500 font-medium text-sm mb-8 leading-relaxed">
              {processing 
                ? `Processando: ${currentContact}` 
                : `Encontramos ${contacts.length} contatos com endereço mas sem coordenadas. Deseja buscar a localização deles agora?`}
            </p>

            {!processing && (
              <div className="flex gap-3">
                 <button onClick={onClose} className="flex-1 py-4 text-slate-400 font-black text-xs uppercase hover:bg-slate-50 dark:hover:bg-navy-800 rounded-xl transition-all">Cancelar</button>
                 <button onClick={startGeocoding} className="flex-1 bg-navy-950 dark:bg-parliament-600 text-white rounded-xl py-4 font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 hover:scale-105 transition-all">
                    <Play size={16} fill="currentColor" /> Iniciar
                 </button>
              </div>
            )}
          </>
        ) : (
          <div className="animate-in zoom-in">
             <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={40} /></div>
             <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-2">Processo Finalizado!</h3>
             <div className="flex justify-center gap-8 my-6">
                <div className="text-center">
                   <p className="text-3xl font-black text-emerald-600">{results.success}</p>
                   <p className="text-[10px] font-bold text-slate-400 uppercase">Sucessos</p>
                </div>
                <div className="text-center">
                   <p className="text-3xl font-black text-red-500">{results.failed}</p>
                   <p className="text-[10px] font-bold text-slate-400 uppercase">Falhas</p>
                </div>
             </div>
             <button onClick={() => { onComplete(); onClose(); }} className="w-full bg-navy-950 dark:bg-parliament-600 text-white rounded-xl py-4 font-black text-xs uppercase tracking-widest shadow-xl">Concluir</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default BulkGeocodeModal;